import "rsuite/dist/rsuite.css";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Form, Container, Row, Col, Table, Button, Modal } from 'react-bootstrap';
import { renderAdvancedPagination, renderPagination, formatNumberWithCommas } from '../utilities';
import { DateRangePicker } from 'rsuite';
import debounce from 'lodash/debounce';
import { useNavigate } from 'react-router-dom';
import { clearSession } from '../AutoLogoutWrapper';

// Every field here can now be added / changed / deleted independently,
// regardless of what other fields are set. No more sequential gating.
// Note: PaymentDate is deliberately NOT in this list. The table has a
// read-only Maturity Date column sitting between Purchase and Payment in
// the header row, so PaymentDate is rendered as its own cell further down
// (after Maturity Date) to keep columns aligned with the headers.
const STAGE_FIELDS = [
    { key: 'InHand', label: 'In Hand' },
    { key: 'ForwardToParty', label: 'To Party' },
    { key: 'ReceivedDoc', label: 'Received Doc.' },
    { key: 'ForwardToBank', label: 'To Bank' },
    { key: 'BankAcceptance', label: 'Bank Acc.' },
    { key: 'PurchaseDate', label: 'Purchase' }
];

// Where the LC is now: the furthest date filled in. That cell is coloured so the
// stage can be read at a glance; the colour moves on when the next date is set.
const STAGE_COLORS = {
    InHand: '#fff3cd',
    ForwardToParty: '#ffe5d0',
    ReceivedDoc: '#e9d8fd',
    ForwardToBank: '#cfe2ff',
    BankAcceptance: '#d2f4ea',
    PurchaseDate: '#d1e7dd',
    MaturityDate: '#fde2f3',
    PaymentDate: '#c7eed8'
};
const STAGE_ORDER = [...STAGE_FIELDS, { key: 'MaturityDate', label: 'Maturity' }, { key: 'PaymentDate', label: 'Payment' }];

function activeStageKey(row) {
    const reached = STAGE_ORDER.filter(s => row[s.key] && row[s.key] !== 0);
    return reached.length ? reached[reached.length - 1].key : null;
}

const MAX_PAGE_SIZE = 500;

// Page-footer buckets, named as in the commercial team's LC statement. Each LC
// is counted once, under the furthest stage it has reached.
const STAGE_BUCKETS = [
    { key: 'lcInHand', label: 'LC In Hand' },
    { key: 'partyAccep', label: 'Party Accep' },
    { key: 'partyBankAcc', label: 'Party Bank Acc' },
    { key: 'purchase', label: 'Purchase' },
    { key: 'matured', label: 'Matured' },
    { key: 'received', label: 'Received' }
];

const isSet = (v) => !!v && v !== 0;

function currentStage(row) {
    if (isSet(row.PaymentDate)) return 'received';
    if (isSet(row.MaturityDate)) return 'matured';
    if (isSet(row.PurchaseDate)) return 'purchase';
    if (isSet(row.BankAcceptance) || isSet(row.ForwardToBank)) return 'partyBankAcc';
    if (isSet(row.ReceivedDoc) || isSet(row.ForwardToParty)) return 'partyAccep';
    return 'lcInHand';
}

function DateUpdateModal({ show, handleClose, handleSave, selectedDate, setSelectedDate }) {
    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton style={{ backgroundColor: '#e9ecef' }}>
                <Modal.Title style={{ fontSize: '16px', fontWeight: 'bold' }}>Set New Date Information</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '30px' }}>
                <Row className="align-items-center">
                    <Col md={3} className="text-end fw-bold">
                        Date <span className="text-danger">*</span> :
                    </Col>
                    <Col md={8}>
                        <Form.Control
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={handleClose} style={{ border: '1px solid #ccc' }}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function UpNoUpdateModal({ show, handleClose, handleSave, upNo, setUpNo }) {
    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton style={{ backgroundColor: '#e9ecef' }}>
                <Modal.Title style={{ fontSize: '16px', fontWeight: 'bold' }}>Set UP No.</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '30px' }}>
                <Row className="align-items-center">
                    <Col md={4} className="text-end fw-bold">
                        UP No. <span className="text-danger">*</span> :
                    </Col>
                    <Col md={8}>
                        <Form.Control
                            type="text"
                            value={upNo}
                            onChange={(e) => setUpNo(e.target.value)}
                            placeholder="Enter UP No."
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={handleClose} style={{ border: '1px solid #ccc' }}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function ExportPartyModal({ show, handleClose, handleExport, partyOptions, selectedParty, setSelectedParty, exporting }) {
    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton style={{ backgroundColor: '#e9ecef' }}>
                <Modal.Title style={{ fontSize: '16px', fontWeight: 'bold' }}>Export Summary for a Party</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '30px' }}>
                <Row className="align-items-center">
                    <Col md={4} className="text-end fw-bold">
                        Party <span className="text-danger">*</span> :
                    </Col>
                    <Col md={8}>
                        <Form.Select
                            value={selectedParty}
                            onChange={(e) => setSelectedParty(e.target.value)}
                        >
                            <option value="">Select a party</option>
                            {partyOptions.map((p, i) => <option key={i} value={p}>{p}</option>)}
                        </Form.Select>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={handleClose} style={{ border: '1px solid #ccc' }}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleExport} disabled={!selectedParty || exporting}>
                    {exporting ? 'Exporting...' : 'Export'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function CommercialTracking() {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [trackingData, setTrackingData] = useState([]);
    const [pageSize, setPageSize] = useState(20);
    const [pageSizeInput, setPageSizeInput] = useState('20');

    const [filters, setFilters] = useState({
        paymentStatus: 'Non Payment',
        teamname: '',
        partyname: '',
        partyBank: '',
        lcNo: '',
        upNo: '',
        stage: ''
    });
    const [dateRange, setDateRange] = useState([]);

    const [teamOptions, setTeamOptions] = useState([]);
    const [partyOptions, setPartyOptions] = useState([]);
    const [partyBankOptions, setPartyBankOptions] = useState([]);

    const [showDateModal, setShowDateModal] = useState(false);
    const [showUpModal, setShowUpModal] = useState(false);
    const [activeRecord, setActiveRecord] = useState(null);
    const [selectedDate, setSelectedDate] = useState('');
    const [upNoInput, setUpNoInput] = useState('');

    const [showExportModal, setShowExportModal] = useState(false);
    const [exportPartyName, setExportPartyName] = useState('');
    const [exportingAll, setExportingAll] = useState(false);
    const [exportingParty, setExportingParty] = useState(false);

    useEffect(() => {
        fetchDropdownOptions();
    }, []);

    const fetchDropdownOptions = async () => {
        try {
            const [teamsRes, partiesRes, banksRes] = await Promise.all([
                axios.get(`${window.backendUrl}/get_all_teams`),
                axios.get(`${window.backendUrl}/get_all_parties`),
                axios.get(`${window.backendUrl}/get_all_party_banks`)
            ]);
            setTeamOptions(teamsRes.data.data.map(t => t.Name));
            setPartyOptions(partiesRes.data.data.map(p => p.PartyName));
            setPartyBankOptions(banksRes.data.data.map(b => b.Name));
        } catch (error) {
            console.error('Error fetching dropdowns:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const handleDateRangeChange = (value) => {
        setDateRange(value && value.length === 2 ? value : []);
        setCurrentPage(1);
    };

    const fetchData = (page, filters, dateRange, limit) => {
        const startDate = dateRange[0] || '';
        const endDate = dateRange[1] || '';

        axios.get(`${window.backendUrl}/get_commercial_tracking`, {
            params: { ...filters, page, limit, startDate, endDate },
            withCredentials: true
        })
        .then((response) => {
            setTrackingData(response.data.data || []);
            setCurrentPage(response.data.currentPage || 1);
            setTotalPages(response.data.totalPages || 1);
            setTotalRecords(response.data.totalRecords || 0);
        })
        .catch((error) => {
            if (error.response?.status === 401) {
                clearSession(); // keeps the AI Office preferences
                navigate('/');
            } else {
                console.error(error);
            }
        });
    };

    const debouncedFetchData = useCallback(
        debounce((page, filters, dateRange, limit) => fetchData(page, filters, dateRange, limit), 300),
        []
    );

    useEffect(() => {
        debouncedFetchData(currentPage, filters, dateRange, pageSize);
        return () => debouncedFetchData.cancel();
    }, [filters, currentPage, dateRange, pageSize, debouncedFetchData]);

    const handlePageSizeChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        setPageSizeInput(raw);
        const size = parseInt(raw, 10);
        if (size >= 1 && size <= MAX_PAGE_SIZE) {
            setPageSize(size);
            setCurrentPage(1);
        }
    };

    const firstRowNo = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const lastRowNo = Math.min(currentPage * pageSize, totalRecords);

    // An LC can appear twice on a page when its party exists under two teams;
    // count each tracking record once.
    const pageTotals = useMemo(() => {
        const sums = { total: 0 };
        STAGE_BUCKETS.forEach(b => { sums[b.key] = 0; });
        const seen = new Set();
        trackingData.forEach(row => {
            if (seen.has(row.CommercialTrackingId)) return;
            seen.add(row.CommercialTrackingId);
            const value = Number(row.LcValue) || 0;
            sums[currentStage(row)] += value;
            sums.total += value;
        });
        return sums;
    }, [trackingData]);

    const formatUsd = (n) => `$${formatNumberWithCommas((Number(n) || 0).toFixed(2))}`;

    const handleActionClick = (trackingId, fieldName) => {
        setActiveRecord({ id: trackingId, field: fieldName });
        setSelectedDate(new Date().toISOString().split('T')[0]);
        setShowDateModal(true);
    };

    const handleUpNoClick = (trackingId, currentUpNo) => {
        setActiveRecord({ id: trackingId, field: 'UP' });
        setUpNoInput(currentUpNo || '');
        setShowUpModal(true);
    };

    const handleSaveDate = () => {
        if (!activeRecord || !selectedDate) return;

        const timestamp = Math.floor(new Date(selectedDate).getTime() / 1000);

        axios.post(`${window.backendUrl}/update_commercial_tracking_date`, {
            trackingId: activeRecord.id,
            field: activeRecord.field,
            dateValue: timestamp
        }, { withCredentials: true })
        .then(response => {
            setShowDateModal(false);
            fetchData(currentPage, filters, dateRange, pageSize);
        })
        .catch(error => {
            console.error('Error updating date:', error);
            setShowDateModal(false);
        });
    };

    const handleSaveUpNo = () => {
        if (!activeRecord) return;

        axios.post(`${window.backendUrl}/update_commercial_tracking_up`, {
            trackingId: activeRecord.id,
            upNo: upNoInput
        }, { withCredentials: true })
        .then(response => {
            setShowUpModal(false);
            fetchData(currentPage, filters, dateRange, pageSize);
        })
        .catch(error => {
            console.error('Error updating UP No:', error);
            setShowUpModal(false);
        });
    };

    const handleDeleteDate = (trackingId, fieldName, label) => {
        if (!window.confirm(`Clear the ${label} date?`)) return;

        axios.post(`${window.backendUrl}/delete_commercial_tracking_date`, {
            trackingId,
            field: fieldName
        }, { withCredentials: true })
        .then(() => {
            fetchData(currentPage, filters, dateRange, pageSize);
        })
        .catch(error => {
            console.error(`Error deleting ${fieldName}:`, error);
        });
    };

    const handleDeleteUpNo = (trackingId) => {
        if (!window.confirm('Clear the UP No.?')) return;

        axios.post(`${window.backendUrl}/update_commercial_tracking_up`, {
            trackingId,
            upNo: ''
        }, { withCredentials: true })
        .then(() => {
            fetchData(currentPage, filters, dateRange, pageSize);
        })
        .catch(error => {
            console.error('Error clearing UP No.:', error);
        });
    };

    const downloadBlob = (data, filename) => {
        const url = window.URL.createObjectURL(new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleExportAll = () => {
        setExportingAll(true);
        axios.get(`${window.backendUrl}/export_commercial_tracking`, {
            responseType: 'blob',
            withCredentials: true
        })
        .then((response) => {
            downloadBlob(response.data, 'Commercial_Tracking_All.xlsx');
        })
        .catch((error) => {
            console.error('Error exporting all parties:', error);
            alert('Failed to generate the Excel file. Please try again.');
        })
        .finally(() => setExportingAll(false));
    };

    const handleExportParty = () => {
        if (!exportPartyName) return;
        setExportingParty(true);
        axios.get(`${window.backendUrl}/export_commercial_tracking`, {
            params: { partyname: exportPartyName },
            responseType: 'blob',
            withCredentials: true
        })
        .then((response) => {
            const safeName = exportPartyName.replace(/[^a-z0-9]/gi, '_');
            downloadBlob(response.data, `Commercial_Tracking_${safeName}.xlsx`);
            setShowExportModal(false);
            setExportPartyName('');
        })
        .catch((error) => {
            console.error('Error exporting party:', error);
            alert('Failed to generate the Excel file. Please try again.');
        })
        .finally(() => setExportingParty(false));
    };

    const formatUnixDate = (unixTime) => {
        if (!unixTime) return null;
        return new Date(unixTime * 1000).toLocaleDateString('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        }).replace(/\//g, '.');
    };

    // Any field can be added at any time, and any filled field can be
    // changed or deleted at any time - no more sequential gating.
    const renderActionCell = (record, fieldKey, btnLabel) => {
        const val = record[fieldKey];

        if (val && val !== 0) {
            return (
                <div className="text-center">
                    <div style={activeStageKey(record) === fieldKey ? { fontWeight: 'bold', fontSize: '13px' } : undefined}>{formatUnixDate(val)}</div>
                    <div className="d-flex justify-content-center gap-1 mt-1">
                        <Button
                            variant="light"
                            size="sm"
                            style={{ fontSize: '11px', border: '1px solid #ccc', backgroundColor: '#fff' }}
                            onClick={() => handleActionClick(record.CommercialTrackingId, fieldKey)}>
                            Change
                        </Button>
                        <Button
                            variant="light"
                            size="sm"
                            title={`Delete ${btnLabel}`}
                            style={{ border: '1px solid #ccc', backgroundColor: '#fff', color: '#dc3545' }}
                            onClick={() => handleDeleteDate(record.CommercialTrackingId, fieldKey, btnLabel)}>
                            <i className="fa fa-trash"></i>
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div className="text-center">
                <Button
                    variant="light"
                    size="sm"
                    style={{ border: '1px solid #ccc', backgroundColor: '#fff' }}
                    onClick={() => handleActionClick(record.CommercialTrackingId, fieldKey)}>
                    {btnLabel}
                </Button>
            </div>
        );
    };

    return (
        <div style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
            <NavigationBar />
            <Container fluid style={{ paddingBottom: '20px' }}>
                <Row style={{ marginTop: '20px', marginBottom: '10px', alignItems: 'center' }}>
                    <Col md={3} className="d-flex align-items-center gap-2">
                        <h4 style={{ margin: 0 }}>Commercial Tracking</h4>
                    </Col>
                    <Col md={3} className="d-flex gap-2">
                        <Button
                            variant="outline-success"
                            size="sm"
                            onClick={handleExportAll}
                            disabled={exportingAll}>
                            <i className="fa fa-file-excel-o me-1"></i> {exportingAll ? 'Exporting...' : 'Export All'}
                        </Button>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setShowExportModal(true)}>
                            <i className="fa fa-file-excel-o me-1"></i> Export by Party
                        </Button>
                    </Col>
                    <Col md={6} className="text-end d-flex justify-content-end align-items-center gap-2">
                        <Form.Control
                            type="text"
                            inputMode="numeric"
                            value={pageSizeInput}
                            onChange={handlePageSizeChange}
                            onBlur={() => setPageSizeInput(String(pageSize))}
                            title={`Rows per page (1-${MAX_PAGE_SIZE})`}
                            style={{ width: '70px', textAlign: 'center' }}
                        />
                        <span className="fw-bold text-nowrap" style={{ fontSize: '14px' }}>
                            {firstRowNo}-{lastRowNo}/{totalRecords}
                        </span>
                        {renderPagination({ totalPages, currentPage, setCurrentPage })}
                        {renderAdvancedPagination({ totalPages, currentPage, setCurrentPage })}
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form className="d-flex justify-content-between w-100 align-items-center">
                            <Form.Select style={{ flex: '1', maxWidth: '170px' }} name="paymentStatus" value={filters.paymentStatus} onChange={handleInputChange}>
                                <option value="Non Payment">Non Payment</option>
                                <option value="Payment">Payment</option>
                            </Form.Select>

                            <div style={{ flex: '1', maxWidth: '220px' }}>
                                <DateRangePicker placeholder="From to Todate" onChange={handleDateRangeChange} style={{ width: '100%' }} />
                            </div>

                            <Form.Select style={{ flex: '1', maxWidth: '160px' }} name="teamname" value={filters.teamname} onChange={handleInputChange}>
                                <option value="">All Team</option>
                                {teamOptions.map((t, i) => <option key={i} value={t}>{t}</option>)}
                            </Form.Select>

                            <Form.Select style={{ flex: '1', maxWidth: '180px' }} name="partyname" value={filters.partyname} onChange={handleInputChange}>
                                <option value="">All Party</option>
                                {partyOptions.map((p, i) => <option key={i} value={p}>{p}</option>)}
                            </Form.Select>

                            <Form.Select style={{ flex: '1', maxWidth: '180px' }} name="partyBank" value={filters.partyBank} onChange={handleInputChange}>
                                <option value="">All Party Bank</option>
                                {partyBankOptions.map((b, i) => <option key={i} value={b}>{b}</option>)}
                            </Form.Select>

                            <Form.Select style={{ flex: '1', maxWidth: '160px' }} name="stage" value={filters.stage} onChange={handleInputChange}>
                                <option value="">All Stage</option>
                                {STAGE_ORDER.filter(s => s.key !== 'PaymentDate').map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                            </Form.Select>

                            <Form.Control style={{ flex: '1', maxWidth: '160px' }} type="text" name="lcNo" value={filters.lcNo} onChange={handleInputChange} placeholder="LC No." />

                            <div className="d-flex" style={{ flex: '1', maxWidth: '160px' }}>
                                <Form.Control type="text" name="upNo" value={filters.upNo} onChange={handleInputChange} placeholder="UP" />
                                <Button variant="light" style={{ border: '1px solid #ced4da', borderLeft: 'none' }}><i className="fa fa-search"></i></Button>
                            </div>
                        </Form>
                    </Col>
                </Row>

                <Row className="mb-2">
                    <Col className="d-flex flex-wrap align-items-center gap-2" style={{ fontSize: '12px' }}>
                        <span className="text-muted">Current stage:</span>
                        {STAGE_ORDER.map(s => (
                            <span key={s.key} style={{ backgroundColor: STAGE_COLORS[s.key], border: '1px solid #ccc', borderRadius: '4px', padding: '1px 8px' }}>{s.label}</span>
                        ))}
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Table bordered hover responsive style={{ fontSize: '12px', backgroundColor: '#fff', border: '1px solid #dee2e6' }}>
                            <thead style={{ backgroundColor: '#d9edf7', textAlign: 'center', verticalAlign: 'middle' }}>
                                <tr>
                                    <th>Team</th>
                                    <th>SL#</th>
                                    <th>Party Name</th>
                                    <th>PI Info</th>
                                    <th>L/C No. /Date</th>
                                    <th>In Hand</th>
                                    <th>To Party</th>
                                    <th>Received Doc</th>
                                    <th>To Bank</th>
                                    <th>Bank Acceptance</th>
                                    <th>Purchase Date</th>
                                    <th>Maturity Date</th>
                                    <th>Payment Date</th>
                                    <th>Expiry Date</th>
                                    <th>UD</th>
                                    <th>UP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trackingData.length > 0 ? trackingData.map((row, index) => (
                                    <tr key={index} style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                        <td>{row.TeamName}</td>
                                        <td>{row.CommercialTrackingId}</td>
                                        <td>{row.PartyName}</td>
                                        <td style={{ fontSize: '11px', textAlign: 'left', width: '180px' }}>
                                            {row.PiInfo ? row.PiInfo.split(',').join(', ') : '-'}
                                        </td>
                                        <td>
                                            <div>{row.LcNo}</div>
                                            <div className="text-danger">{row.AltLcNo}</div>
                                            {(row.AmendmentInfo || '').trim() && (<div style={{ fontSize: '11px', color: '#6c757d' }} title="L/C amendment">Amend: {row.AmendmentInfo.trim()}{Number(row.AmendmentDate) > 0 && ` · ${new Date(row.AmendmentDate * 1000).toLocaleString('en-GB', { timeZone: 'Asia/Dhaka', day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '.')}`}</div>)}
                                            <div>{formatUnixDate(row.LcDate)}</div>
                                            <div className="fw-bold">{formatUsd(row.LcValue)}</div>
                                        </td>
                                        {STAGE_ORDER.map(stage => (
                                            <td key={stage.key} style={activeStageKey(row) === stage.key ? { backgroundColor: STAGE_COLORS[stage.key] } : undefined}>
                                                {renderActionCell(row, stage.key, stage.label)}
                                            </td>
                                        ))}
                                        <td>
                                            {formatUnixDate(row.ExpiredDate)}
                                            <br />
                                            <i className="fa fa-calendar text-info"></i>
                                        </td>
                                        <td>
                                            {row.UD && row.UD !== 0 ? (
                                                <div className="text-center">
                                                    <div>{formatUnixDate(row.UD)}</div>
                                                    <div className="d-flex justify-content-center gap-1 mt-1">
                                                        <Button variant="light" size="sm" style={{ fontSize: '11px', border: '1px solid #ccc', backgroundColor: '#fff' }} onClick={() => handleActionClick(row.CommercialTrackingId, 'UD')}>
                                                            Change
                                                        </Button>
                                                        <Button variant="light" size="sm" title="Delete UD" style={{ border: '1px solid #ccc', backgroundColor: '#fff', color: '#dc3545' }} onClick={() => handleDeleteDate(row.CommercialTrackingId, 'UD', 'UD')}>
                                                            <i className="fa fa-trash"></i>
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <Button variant="light" size="sm" style={{ border: '1px solid #ccc', backgroundColor: '#fff' }} onClick={() => handleActionClick(row.CommercialTrackingId, 'UD')}>
                                                    UD Date
                                                </Button>
                                            )}
                                        </td>
                                        <td>
                                            {(row.UP || row.UpNo) ? (
                                                <div className="text-center">
                                                    <div>{row.UP || row.UpNo}</div>
                                                    <div className="d-flex justify-content-center gap-1 mt-1">
                                                        <Button variant="light" size="sm" style={{ fontSize: '11px', border: '1px solid #ccc', backgroundColor: '#fff' }} onClick={() => handleUpNoClick(row.CommercialTrackingId, row.UP || row.UpNo)}>
                                                            Change
                                                        </Button>
                                                        <Button variant="light" size="sm" title="Delete UP" style={{ border: '1px solid #ccc', backgroundColor: '#fff', color: '#dc3545' }} onClick={() => handleDeleteUpNo(row.CommercialTrackingId)}>
                                                            <i className="fa fa-trash"></i>
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <Button variant="light" size="sm" style={{ border: '1px solid #ccc', backgroundColor: '#fff' }} onClick={() => handleUpNoClick(row.CommercialTrackingId, '')}>
                                                    UP No.
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="16" className="text-center py-4 text-muted">No records found.</td>
                                    </tr>
                                )}
                                {trackingData.length > 0 && (
                                    <tr style={{ verticalAlign: 'middle', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f1f5f9' }}>
                                        <td colSpan="4" className="text-end">Page total ({firstRowNo}-{lastRowNo}):</td>
                                        <td>{formatUsd(pageTotals.total)}</td>
                                        {/* Spans line up with the header: In Hand | To Party + Received Doc |
                                            To Bank + Bank Acceptance | Purchase | Maturity | Payment */}
                                        {[
                                            ['lcInHand', 1], ['partyAccep', 2], ['partyBankAcc', 2],
                                            ['purchase', 1], ['matured', 1], ['received', 1]
                                        ].map(([key, span]) => (
                                            <td key={key} colSpan={span}>
                                                <div style={{ fontSize: '10px', fontWeight: 'normal' }}>
                                                    {STAGE_BUCKETS.find(b => b.key === key).label}
                                                </div>
                                                <div>{formatUsd(pageTotals[key])}</div>
                                            </td>
                                        ))}
                                        <td colSpan="3"></td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>

            <DateUpdateModal
                show={showDateModal}
                handleClose={() => setShowDateModal(false)}
                handleSave={handleSaveDate}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
            />

            <UpNoUpdateModal
                show={showUpModal}
                handleClose={() => setShowUpModal(false)}
                handleSave={handleSaveUpNo}
                upNo={upNoInput}
                setUpNo={setUpNoInput}
            />

            <ExportPartyModal
                show={showExportModal}
                handleClose={() => { setShowExportModal(false); setExportPartyName(''); }}
                handleExport={handleExportParty}
                partyOptions={partyOptions}
                selectedParty={exportPartyName}
                setSelectedParty={setExportPartyName}
                exporting={exportingParty}
            />
        </div>
    );
}

export default CommercialTracking;